import React from 'react';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { Box, ButtonV2, Divider, Flex, TextV2 } from '@withjoy/joykit';
import { JoyLogo } from '@assets/index';
import { useTranslation } from '@shared/core';
import { FindHotelMatchQuery } from '@graphql/generated';
import { pxToRem } from '@withjoy/joykit/theme';
import { HOTEL_IMAGE_PLACEHOLDER } from '@shared/utils/constants';
import { withWindow } from '@shared/utils/withWindow';

type HotelSuggestionDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSaveLink: () => void;
  onKeepOriginalLink: () => void;
  hotelMatch: FindHotelMatchQuery['findHotelMatch'] | null;
  links: {
    originalLink?: string;
    suggestedLink?: string;
  };
};

const HotelSuggestionDialog = ({ isOpen, onClose, onSaveLink, onKeepOriginalLink, hotelMatch, links }: HotelSuggestionDialogProps) => {
  const { t } = useTranslation('websiteEditor');
  const translations = t('customPage', 'richText', 'hotelSuggestionDialog');

  const openSuggestedLink = () => {
    if (links.suggestedLink) {
      withWindow(global => {
        global.open(links.suggestedLink, '_blank');
      });
    }
  };

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={onClose}
      hideDivider={true}
      removeBodyPadding={true}
      renderFooter={() => (
        <Flex
          flexDirection="column"
          gap={4}
          justifyContent="center"
          alignItems="center"
          width="100%"
        >
          <Box>
            <ButtonV2
              shape="rounded"
              intent="newPrimary"
              onClick={onSaveLink}
            >
              {translations.saveLinkCta()}
            </ButtonV2>
          </Box>
          <Box>
            <ButtonV2
              variant="ghost"
              onClick={onKeepOriginalLink}
              color="mono14"
            >
              {translations.keepOriginalLinkCta()}
            </ButtonV2>
          </Box>
        </Flex>
      )}
    >
      <Flex
        justifyContent="center"
        alignItems="center"
        width="100%"
      >
        <Flex
          flexDirection="column"
          gap={4}
          width="80%"
          justifyContent="center"
          alignItems="center"
        >
          <TextV2
            textAlign="center"
            typographyVariant="hed5"
            fontWeight={600}
          >
            {translations.title()}
          </TextV2>

          <TextV2
            textAlign="center"
            typographyVariant="body1"
          >
            {translations.description()}
          </TextV2>

          <Flex
            gap={4}
            flexDirection="column"
            boxShadow="0px 6.15px 23.72px 0px rgba(44, 41, 37, 0.06), 0px 3.514px 14.056px 0px rgba(0, 0, 0, 0.07)"
            borderRadius="10px"
            padding={4}
            width={pxToRem(300)}
            onClick={openSuggestedLink}
            cursor="pointer"
            role="link"
          >
            <Box
              backgroundImage={`url(${hotelMatch?.thumbnailUrl || HOTEL_IMAGE_PLACEHOLDER})`}
              backgroundSize="cover"
              backgroundPosition="center"
              backgroundRepeat="no-repeat"
              height="10rem"
              minHeight="10rem"
              borderRadius={pxToRem(14)}
              width="100%"
            />
            {hotelMatch?.name && (
              <TextV2
                typographyVariant="body1"
                fontWeight={600}
              >
                {translations.value({ value: hotelMatch?.name })}
              </TextV2>
            )}
            <Divider />
            <Flex
              gap={4}
              justifyContent="space-between"
            >
              <JoyLogo style={{ height: '24px' }} />
              {hotelMatch?.rate && (
                <Flex
                  gap={4}
                  alignItems="center"
                >
                  <TextV2 typographyVariant="label2">{translations.guestOnlyPrice()}</TextV2>
                  <TextV2 typographyVariant="hed4">{translations.value({ value: hotelMatch?.rate })}</TextV2>
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </ResponsiveDialog>
  );
};

export default HotelSuggestionDialog;
